import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';

// Add global refresh function on window for cross-component access
declare global {
  interface Window {
    refreshNotifications?: () => Promise<void>;
  }
}

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  actionUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  fetchNotifications: () => Promise<void>;
  markAsRead: (ids: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  fetchNotifications: async () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  deleteNotification: async () => {}
});

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch notifications on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchNotifications();
      
      // Set up polling for new notifications (every 30 seconds)
      const intervalId = setInterval(() => {
        fetchNotifications();
      }, 30000);
      
      // Make the fetchNotifications function globally accessible
      window.refreshNotifications = fetchNotifications;
      
      return () => {
        clearInterval(intervalId);
        // Clean up global function reference on unmount
        window.refreshNotifications = undefined;
      };
    }
  }, [user]); // Removed fetchNotifications from dependencies

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiRequest('GET', '/api/notifications');
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }
      
      const responseData = await response.json();
      
      // Handle the response format: { success: boolean, data: Notification[] }
      if (responseData && responseData.success === true) {
        // Extract the actual notifications array from the data property
        const notificationsArray = responseData.data || [];
        setNotifications(notificationsArray);
        
        // Calculate unread count
        const unread = notificationsArray.filter((notification: Notification) => !notification.isRead).length;
        setUnreadCount(unread);
      } else {
        // Handle unexpected response format
        console.error('Unexpected API response format:', responseData);
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
      setError(err.message || 'Failed to fetch notifications');
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const markAsRead = useCallback(async (ids: string[]) => {
    if (!ids.length || !user) return;
    
    try {
      // The API endpoint expects the notification IDs wrapped in a proper request body object
      const response = await apiRequest('POST', '/api/notifications/read', { ids });
      
      if (!response.ok) {
        throw new Error('Failed to mark notifications as read');
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          ids.includes(notification.id) ? { ...notification, isRead: true } : notification
        )
      );
      
      // Recalculate unread count
      setUnreadCount(prev => Math.max(0, prev - ids.length));
      
    } catch (err: any) {
      console.error('Error marking notifications as read:', err);
      toast({
        title: "Failed to mark as read",
        description: err.message || "There was an issue updating notifications",
        variant: "destructive",
      });
    }
  }, [user, toast]);

  const markAllAsRead = useCallback(async () => {
    if (!user || unreadCount === 0) return;
    
    try {
      const response = await apiRequest('POST', '/api/notifications/read-all');
      
      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );
      
      // Reset unread count
      setUnreadCount(0);
      
      toast({
        title: "All notifications marked as read",
        variant: "default",
      });
      
    } catch (err: any) {
      console.error('Error marking all notifications as read:', err);
      toast({
        title: "Failed to mark all as read",
        description: err.message || "There was an issue updating notifications",
        variant: "destructive",
      });
    }
  }, [user, unreadCount, toast]);

  const deleteNotification = useCallback(async (id: string) => {
    if (!user) return;
    
    try {
      const response = await apiRequest('DELETE', `/api/notifications/${id}`);
      
      if (!response.ok) {
        throw new Error('Failed to delete notification');
      }
      
      // Update local state
      const updatedNotifications = notifications.filter(notification => notification.id !== id);
      setNotifications(updatedNotifications);
      
      // Recalculate unread count if needed
      const deletedNotification = notifications.find(n => n.id === id);
      if (deletedNotification && !deletedNotification.isRead) {
        setUnreadCount(prev => prev - 1);
      }
      
    } catch (err: any) {
      console.error('Error deleting notification:', err);
      toast({
        title: "Failed to delete notification",
        description: err.message || "There was an issue deleting the notification",
        variant: "destructive",
      });
    }
  }, [user, notifications, toast]);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);